import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Badge, type BadgeAnimationEndPayload, type BadgeAnimationStartPayload } from './progress/Badge';
import { DynamicProblemTree, ProblemTreeData } from './DynamicProblemTree';
import { Lock } from 'lucide-react';
import type { AchievementType } from './MathEducationProvider';
import {
  generateProblemTreeStructure,
  createAchievementIndexMapping,
  validateAchievementDisplayOrder,
  getAchievementMetadata,
} from './achievements/achievementMetadata';
import { Panel } from './ui/panel';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

// Validate achievement display order in development
if (process.env.NODE_ENV === 'development') {
  const validation = validateAchievementDisplayOrder();
  if (!validation.isValid) {
    console.error('[ProblemTreePanel] Achievement display order validation failed:');
    validation.errors.forEach(error => console.error(`  - ${error}`));
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface Row {
  /** Display label for the row (shown on hover) */
  label: string;
  /** Tailwind background color class (e.g. 'bg-blue-500') */
  color: string;
  /** Hex color for glow effects (e.g. '#3b82f6') */
  glow: string;
  /** Maximum number of dots in this row */
  maxCount: number;
  /** Current count (0 to maxCount) */
  count: number;
}

interface BadgeCfg {
  /** Icon content - can be emoji string, image, SVG, or React component */
  icon: React.ReactNode;
  /** Badge title shown in tooltip */
  title: string;
  /** Badge description shown in tooltip */
  description: string;
  /** If true, badge shows as "?" when empty with generic secret text */
  isSecret?: boolean;
  /** Optional hint text shown for unrevealed secret badges (overrides default hint) */
  secretHint?: string;
  /** When true, the badge is hidden entirely until it has a non-`empty` state */
  doubleSecret?: boolean;
  /** Current badge state */
  state: AchievementType;
}

interface BadgeSection {
  /** Section title */
  title: string;
  /** Badges in this section */
  badges: BadgeCfg[];
}

interface AnimatingBadgeInfo {
  badgeId: string;
  index: number;
  type: AchievementType;
  icon: React.ReactNode;
  title: string;
  description: string;
  position: { top: number; left: number; width: number; height: number };
  phase: 'fading-in' | 'active' | 'fading-out';
}

/**
 * ProblemTreePanel - A controlled component for displaying user progress in a problem tree layout
 */
interface ProblemTreePanelProps {
  rows: Row[];
  badgeSections: BadgeSection[];
  isDotsComplete: boolean;
  /** Whether advanced mode should be unlocked (checked via isAdvancedModeUnlocked() in achievementMetadata.ts) */
  isBadgesComplete: boolean;
  onAdvancedModeClick?: () => void;
  selectedGoal: string | null;
  onGoalSelect: (achievementId: string) => void;
  unlockingBadges: Map<string, AchievementType>;
  onBadgeUnlockComplete: (badgeId: string) => void;
  embedded?: boolean;
}

// ============================================================================
// PROBLEM TREE STRUCTURE DEFINITION
// ============================================================================

/**
 * Generate the problem tree structure from achievement prerequisites
 *
 * SINGLE SOURCE OF TRUTH: The tree structure is automatically generated from
 * the prerequisites defined in achievementMetadata.ts. This prevents
 * inconsistencies between the visual tree and the actual achievement dependencies.
 *
 * To modify the tree structure, update the prerequisites in achievementMetadata.ts
 */
const PROBLEM_TREE_STRUCTURE: ProblemTreeData = {
  nodes: generateProblemTreeStructure(),
};

/**
 * Map badge IDs to their array indices
 * AUTOMATICALLY GENERATED from achievementMetadata.ts
 * This ensures the mapping stays in sync with the achievement metadata
 */
const BADGE_ID_TO_INDEX: Record<string, number> = createAchievementIndexMapping();

// ============================================================================
// MAIN PROBLEM TREE PANEL (with dynamic layout)
// ============================================================================
const ProblemTreePanel: React.FC<ProblemTreePanelProps> = ({
  badgeSections,
  isBadgesComplete,
  onAdvancedModeClick,
  selectedGoal,
  onGoalSelect,
  unlockingBadges,
  onBadgeUnlockComplete,
  embedded = false,
}) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const lockRef = React.useRef<HTMLDivElement>(null);

  // Advanced mode unlock status is passed from parent (App.tsx)
  // which uses isAdvancedModeUnlocked() from achievementMetadata.ts
  // to check if all advanced-button prerequisites are met
  const isUnlocked: boolean = isBadgesComplete;

  // Track all concurrently animating badges for spotlight effect.
  const [animatingBadges, setAnimatingBadges] = useState<AnimatingBadgeInfo[]>([]);
  const fadeInTimersRef = React.useRef<Map<string, number>>(new Map());
  const fadeOutTimersRef = React.useRef<Map<string, number>>(new Map());
  const canRenderOverlayPortal = typeof document !== 'undefined';

  // Flatten all badges for the animation callback
  const allBadges = useMemo(() => badgeSections.flatMap(section => section.badges), [badgeSections]);

  // Compute display state for each badge: core state OR unlocking state
  // This allows badges to show as unlocked during animation, even though core state hasn't updated yet
  const badgeDisplayStates = useMemo(() => {
    const states = new Map<string, AchievementType>();

    allBadges.forEach((badge, index) => {
      // Find the badge ID for this index
      const badgeId = Object.entries(BADGE_ID_TO_INDEX).find(([, idx]) => idx === index)?.[0];
      if (!badgeId) return;

      // Use unlocking state if present, otherwise use core state
      const unlockingLevel = unlockingBadges.get(badgeId);
      const displayState = unlockingLevel || badge.state;

      states.set(badgeId, displayState);
    });

    return states;
  }, [allBadges, unlockingBadges]);

  const handleBadgeAnimationStart = React.useCallback((payload: BadgeAnimationStartPayload) => {
    const badgeId = payload.badgeId;

    const existingFadeIn = fadeInTimersRef.current.get(badgeId);
    if (existingFadeIn) {
      window.clearTimeout(existingFadeIn);
    }

    const existingFadeOut = fadeOutTimersRef.current.get(badgeId);
    if (existingFadeOut) {
      window.clearTimeout(existingFadeOut);
      fadeOutTimersRef.current.delete(badgeId);
    }

    setAnimatingBadges(prev => {
      const next: AnimatingBadgeInfo = {
        badgeId,
        index: payload.index,
        type: payload.type,
        icon: payload.icon,
        title: payload.title ?? '',
        description: payload.description ?? '',
        position: {
          top: payload.rect.top,
          left: payload.rect.left,
          width: payload.rect.width,
          height: payload.rect.height,
        },
        phase: 'fading-in',
      };

      const existingIndex = prev.findIndex(item => item.badgeId === badgeId);
      if (existingIndex === -1) {
        return [...prev, next];
      }

      const updated = [...prev];
      updated[existingIndex] = next;
      return updated;
    });

    const timerId = window.setTimeout(() => {
      setAnimatingBadges(prev => prev.map(item => (item.badgeId === badgeId ? { ...item, phase: 'active' } : item)));
      fadeInTimersRef.current.delete(badgeId);
    }, 200);
    fadeInTimersRef.current.set(badgeId, timerId);
  }, []);

  const handleBadgeAnimationEnd = React.useCallback((payload: BadgeAnimationEndPayload) => {
    const badgeId = payload.badgeId;
    const existingFadeIn = fadeInTimersRef.current.get(badgeId);
    if (existingFadeIn) {
      window.clearTimeout(existingFadeIn);
      fadeInTimersRef.current.delete(badgeId);
    }

    setAnimatingBadges(prev => prev.map(item => (item.badgeId === badgeId ? { ...item, phase: 'fading-out' } : item)));

    const existingFadeOut = fadeOutTimersRef.current.get(badgeId);
    if (existingFadeOut) {
      window.clearTimeout(existingFadeOut);
    }

    const timerId = window.setTimeout(() => {
      setAnimatingBadges(prev => prev.filter(item => item.badgeId !== badgeId));
      fadeOutTimersRef.current.delete(badgeId);
    }, 300);
    fadeOutTimersRef.current.set(badgeId, timerId);
  }, []);

  React.useEffect(() => {
    return () => {
      fadeInTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
      fadeOutTimersRef.current.forEach(timerId => window.clearTimeout(timerId));
      fadeInTimersRef.current.clear();
      fadeOutTimersRef.current.clear();
    };
  }, []);

  // Function to check if a node is unlocked (for connection styling)
  const isNodeUnlocked = React.useCallback(
    (nodeId: string): boolean => {
      // Advanced button is unlocked when all badges are complete
      if (nodeId === 'advanced-button') {
        return isUnlocked;
      }

      // Check if the badge for this node is unlocked (not empty)
      const badgeIndex = BADGE_ID_TO_INDEX[nodeId];
      if (badgeIndex === undefined) return false;

      const badge = allBadges[badgeIndex];
      return badge ? badge.state !== 'empty' : false;
    },
    [allBadges, isUnlocked],
  );

  // Function to determine which badges should glow (recommended next goals)
  // NOTE: Uses ONLY core badge state, NOT unlocking badges - this prevents glow during animation

  /**
   * Determines if an achievement is recommended (should be highlighted as a next goal)
   *
   * RECOMMENDATION CRITERIA:
   * An achievement is recommended if:
   * 1. It is not yet achieved (empty in core state)
   * 2. At least ONE of its prerequisites has been met (unlocked in core state)
   *    - OR it has no prerequisites (starter achievements are always recommended)
   * 3. Advanced mode has not been unlocked yet
   * 4. The currently selected goal (if any) is already achieved
   *    - If an unachieved goal is selected, hide all recommendations
   *
   * In other words: recommended UNLESS ALL prerequisites are unmet
   *
   * This logic can be updated here to change what gets recommended.
   */
  const isAchievementRecommended = React.useCallback(
    (nodeId: string): boolean => {
      // Don't recommend if badge is currently unlocking
      if (unlockingBadges.has(nodeId)) return false;

      // Don't recommend if advanced mode is already unlocked
      if (isUnlocked) return false;

      // Hide all recommendations if the currently selected goal is unachieved
      if (selectedGoal) {
        const selectedBadgeIndex = BADGE_ID_TO_INDEX[selectedGoal];
        if (selectedBadgeIndex !== undefined) {
          const selectedBadge = allBadges[selectedBadgeIndex];
          if (selectedBadge && selectedBadge.state === 'empty') {
            return false; // Hide recommendations when an unachieved goal is selected
          }
        }
      }

      // Check if this achievement is already unlocked (use core state only)
      const badgeIndex = BADGE_ID_TO_INDEX[nodeId];
      if (badgeIndex === undefined) return false;
      const badge = allBadges[badgeIndex];
      if (!badge || badge.state !== 'empty') return false; // Already achieved

      // Get prerequisites from achievement metadata (NOT from tree structure)
      const metadata = getAchievementMetadata(nodeId);
      if (!metadata) return false;

      const prerequisites = metadata.prerequisites;

      // If no prerequisites, it's a starter achievement - always recommended
      if (prerequisites.length === 0) return true;

      // Recommended if AT LEAST ONE prerequisite is met
      // (i.e., NOT recommended only if ALL prerequisites are unmet)
      return prerequisites.some(prereqId => {
        const prereqIndex = BADGE_ID_TO_INDEX[prereqId];
        if (prereqIndex === undefined) return false;
        const prereqBadge = allBadges[prereqIndex];
        return prereqBadge && prereqBadge.state !== 'empty';
      });
    },
    [allBadges, isUnlocked, unlockingBadges, selectedGoal],
  );

  const shouldBadgeGlow = React.useCallback(
    (nodeId: string): boolean => {
      return isAchievementRecommended(nodeId);
    },
    [isAchievementRecommended],
  );

  // Render function for each node in the problem tree
  const renderNode = React.useCallback(
    (nodeId: string) => {
      // Special case: Advanced Button
      if (nodeId === 'advanced-button') {
        if (!isUnlocked) {
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  ref={lockRef}
                  style={{
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'rgba(200, 200, 210, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Lock style={{ width: '28px', height: '28px', color: 'rgba(100, 100, 120, 0.7)' }} />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <span>Finish the hardest problems to move to advanced practice.</span>
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <div
            style={{
              width: '64px',
              height: '64px',
              position: 'relative',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={() => onAdvancedModeClick?.()}
              className="advanced-mode-button"
              style={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                color: 'var(--primary-foreground)',
                borderRadius: 'var(--radius-button)',
                border: 'none',
                cursor: 'pointer',
                paddingTop: 'var(--spacing-4)',
                paddingBottom: 'var(--spacing-4)',
                paddingLeft: 'var(--spacing-6)',
                paddingRight: 'var(--spacing-6)',
                fontFamily: 'var(--font-atkinson)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-bold)',
                boxShadow: '0 0 30px var(--primary), 0 0 50px var(--accent)',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s',
                animation: 'advancedModePulse 1.5s ease-in-out infinite',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                position: 'absolute',
                overflow: 'hidden',
                left: '50%',
                top: '0',
                transform: 'translateX(-50%)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateX(-50%) scale(1.15)';
                e.currentTarget.style.boxShadow = '0 0 40px var(--primary), 0 0 80px var(--accent)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
                e.currentTarget.style.boxShadow = '0 0 30px var(--primary), 0 0 50px var(--accent)';
                e.currentTarget.style.animation = 'advancedModePulse 1.5s ease-in-out infinite';
              }}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>Practice Unlocked!</span>
              <div
                style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
                  animation: 'shimmer 2.5s infinite',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            </button>
            <style>{`
            @keyframes advancedModePulse {
              0%, 100% { 
                transform: translateX(-50%) scale(1); 
                box-shadow: 0 0 30px var(--primary), 0 0 50px var(--accent);
              }
              50% { 
                transform: translateX(-50%) scale(1.12); 
                box-shadow: 0 0 40px var(--primary), 0 0 70px var(--accent);
              }
            }
            @keyframes shimmer {
              0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
              100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
            }
            .advanced-mode-button::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 50%, rgba(255, 255, 255, 0.3) 100%);
              opacity: 0;
              transition: opacity 0.3s;
              border-radius: var(--radius-button);
            }
            .advanced-mode-button:hover::before {
              opacity: 1;
            }
          `}</style>
          </div>
        );
      }

      // Regular badge
      const badgeIndex = BADGE_ID_TO_INDEX[nodeId];
      if (badgeIndex === undefined) return null;

      const badge = allBadges[badgeIndex];
      if (!badge) return null;

      // Get display state: unlocking state OR core state
      const displayState = badgeDisplayStates.get(nodeId) || badge.state;

      return (
        <div style={{ width: '64px', height: '64px' }}>
          <Badge
            badgeId={nodeId}
            type={displayState}
            icon={badge.icon}
            index={badgeIndex}
            title={badge.title}
            description={badge.description}
            isSecret={badge.isSecret}
            secretHint={badge.secretHint}
            isSelected={selectedGoal === nodeId}
            onClick={() => onGoalSelect(nodeId)}
            onAnimationStart={handleBadgeAnimationStart}
            onAnimationEnd={handleBadgeAnimationEnd}
            shouldGlow={shouldBadgeGlow(nodeId)}
            isUnlocking={unlockingBadges.has(nodeId)}
            onUnlockComplete={onBadgeUnlockComplete}
          />
        </div>
      );
    },
    [
      allBadges,
      badgeDisplayStates,
      isUnlocked,
      onAdvancedModeClick,
      handleBadgeAnimationStart,
      handleBadgeAnimationEnd,
      selectedGoal,
      onGoalSelect,
      shouldBadgeGlow,
      unlockingBadges,
      onBadgeUnlockComplete,
    ],
  );

  return (
    <>
      {animatingBadges.length > 0 &&
        canRenderOverlayPortal &&
        createPortal(
          <>
            {/* Achievement Spotlight Overlay - darkens and blurs everything */}
            <div
              className="tw-fixed tw-inset-0 tw-pointer-events-none tw-transition-all tw-duration-200"
              style={{
                zIndex: 9998,
                backdropFilter: animatingBadges.every(item => item.phase === 'fading-out') ? 'blur(0px)' : 'blur(8px)',
                backgroundColor: animatingBadges.every(item => item.phase === 'fading-out')
                  ? 'rgba(0, 0, 0, 0)'
                  : 'rgba(0, 0, 0, 0.85)',
                opacity: animatingBadges.every(item => item.phase === 'fading-in') ? 0 : 1,
                transition: 'opacity 0.2s ease-out, backdrop-filter 0.2s ease-out, background-color 0.2s ease-out',
              }}
            />

            {/* Highlighted Badge Duplicate - positioned at original location */}
            {animatingBadges.map(animatingBadge => (
              <div
                key={animatingBadge.badgeId}
                className="tw-fixed tw-pointer-events-none"
                style={{
                  zIndex: 9999,
                  top: animatingBadge.position.top,
                  left: animatingBadge.position.left,
                  width: animatingBadge.position.width,
                  height: animatingBadge.position.height,
                }}
              >
                <div
                  className="tw-relative tw-w-full tw-h-full"
                  style={{
                    animation:
                      animatingBadge.phase === 'active' || animatingBadge.phase === 'fading-out'
                        ? 'badgeSpotlight 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                        : 'none',
                  }}
                >
                  {/* Enlarged Badge */}
                  <div
                    className="tw-absolute tw-rounded-lg tw-border-2 tw-flex tw-items-center tw-justify-center"
                    style={{
                      clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
                      width: '100%',
                      height: '100%',
                      background: (() => {
                        switch (animatingBadge.type) {
                          case 'gold':
                            return 'linear-gradient(to bottom right, #fde047, #facc15, #ca8a04)';
                          case 'silver':
                            return 'linear-gradient(to bottom right, #e5e7eb, #d1d5db, #9ca3af)';
                          case 'bronze':
                            return 'linear-gradient(to bottom right, #fb923c, #f97316, #c2410c)';
                          default:
                            return '#e5e7eb';
                        }
                      })(),
                      borderColor: (() => {
                        switch (animatingBadge.type) {
                          case 'gold':
                            return '#eab308';
                          case 'silver':
                            return '#9ca3af';
                          case 'bronze':
                            return '#ea580c';
                          default:
                            return '#9ca3af';
                        }
                      })(),
                      boxShadow: (() => {
                        switch (animatingBadge.type) {
                          case 'gold':
                            return '0 0 40px rgba(250, 204, 21, 0.9), 0 0 80px rgba(250, 204, 21, 0.5), 0 0 120px rgba(250, 204, 21, 0.3)';
                          case 'silver':
                            return '0 0 40px rgba(209, 213, 219, 0.9), 0 0 80px rgba(209, 213, 219, 0.5), 0 0 120px rgba(209, 213, 219, 0.3)';
                          case 'bronze':
                            return '0 0 40px rgba(249, 115, 22, 0.9), 0 0 80px rgba(249, 115, 22, 0.5), 0 0 120px rgba(249, 115, 22, 0.3)';
                          default:
                            return 'none';
                        }
                      })(),
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--font-atkinson)',
                        fontSize: 'var(--text-2xl)',
                      }}
                    >
                      {animatingBadge.icon}
                    </div>
                  </div>

                  {/* Animated rings */}
                  <div
                    className="tw-absolute tw-inset-0 tw-rounded-lg tw-pointer-events-none"
                    style={{
                      clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
                      animation: 'ringPulse 0.6s ease-out',
                      border: '3px solid',
                      borderColor: (() => {
                        switch (animatingBadge.type) {
                          case 'gold':
                            return '#fbbf24';
                          case 'silver':
                            return '#d1d5db';
                          case 'bronze':
                            return '#f97316';
                          default:
                            return '#9ca3af';
                        }
                      })(),
                    }}
                  />

                  {/* Multiple expanding rings for extra effect */}
                  <div
                    className="tw-absolute tw-inset-0 tw-rounded-lg tw-pointer-events-none"
                    style={{
                      clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)',
                      animation: 'ringPulse 0.8s ease-out 0.1s',
                      border: '2px solid',
                      borderColor: (() => {
                        switch (animatingBadge.type) {
                          case 'gold':
                            return '#fbbf24';
                          case 'silver':
                            return '#d1d5db';
                          case 'bronze':
                            return '#f97316';
                          default:
                            return '#9ca3af';
                        }
                      })(),
                    }}
                  />

                  {/* Sparkle effects - positioned relative to badge */}
                  {Array.from({ length: 20 }).map((_, i) => {
                    const angle = (i / 20) * 360;
                    const angleRad = (angle * Math.PI) / 180;
                    const distance = 60 + Math.random() * 40;
                    const delay = i * 0.03;
                    return (
                      <div
                        key={i}
                        className="tw-absolute tw-w-2 tw-h-2"
                        style={
                          {
                            left: '50%',
                            top: '50%',
                            animation: `sparkleFloat 0.8s ease-out ${delay}s`,
                            '--sparkle-x': `${Math.cos(angleRad) * distance}px`,
                            '--sparkle-y': `${Math.sin(angleRad) * distance}px`,
                          } as React.CSSProperties
                        }
                      >
                        <div
                          className="tw-w-full tw-h-full tw-rounded-full"
                          style={{
                            background: 'var(--card)',
                            boxShadow: '0 0 6px rgba(255, 255, 255, 0.9)',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>,
          document.body,
        )}

      <style>{`
        @keyframes badgeSpotlight {
          0% { transform: scale(1) rotate(0deg); }
          20% { transform: scale(1.3) rotate(-5deg); }
          40% { transform: scale(1.15) rotate(3deg); }
          60% { transform: scale(1.25) rotate(-2deg); }
          80% { transform: scale(1.1) rotate(1deg); }
          100% { transform: scale(1.2) rotate(0deg); }
        }
        @keyframes ringPulse {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes sparkleFloat {
          0% { 
            transform: translate(-50%, -50%) scale(0) rotate(0deg); 
            opacity: 1; 
          }
          100% { 
            transform: translate(calc(-50% + var(--sparkle-x)), calc(-50% + var(--sparkle-y))) scale(1) rotate(180deg); 
            opacity: 0; 
          }
        }
      `}</style>
      {embedded ? (
        <div ref={panelRef} style={{ height: '100%', overflow: 'visible' }}>
          {/* Achievements Section - Problem Tree */}
          <div
            className="tw-flex tw-flex-col tw-items-center"
            style={{
              position: 'relative',
              zIndex: 2,
              padding: 'var(--spacing-1)',
              minHeight: 'min-content',
            }}
          >
            <div style={{ marginBottom: '2px' }}>
              <h3
                style={{
                  textAlign: 'center',
                  marginBottom: 0,
                  color: 'var(--muted-foreground)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {/* Problem Tree */}
              </h3>
              <p
                style={{
                  textAlign: 'center',
                  color: 'var(--muted-foreground)',
                  fontFamily: 'var(--font-atkinson)',
                  fontSize: '11px',
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                (Click a badge to set a goal.)
              </p>
            </div>

            {/* Dynamic Problem Tree Container */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
                minHeight: 0,
              }}
            >
              <DynamicProblemTree
                problemData={PROBLEM_TREE_STRUCTURE}
                nodeSize={64}
                horizontalGap={40}
                verticalGap={80}
                renderNode={renderNode}
                isNodeUnlocked={isNodeUnlocked}
                className="tw-max-w-md"
              />
            </div>
          </div>
        </div>
      ) : (
        <Panel
          ref={panelRef}
          className="panel-scrollbar"
          style={{
            height: '100%',
            width: '400px',
            minWidth: '400px',
            maxWidth: '400px',
            flexShrink: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Achievements Section - Problem Tree */}
          <div
            className="tw-flex tw-flex-col tw-items-center"
            style={{
              position: 'relative',
              zIndex: 2,
              padding: 'var(--spacing-1)',
              minHeight: 'min-content',
            }}
          >
            <div style={{ marginBottom: '2px' }}>
              <h3
                style={{
                  textAlign: 'center',
                  marginBottom: 0,
                  color: 'var(--muted-foreground)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {/* Problem Tree */}
              </h3>
              <p
                style={{
                  textAlign: 'center',
                  color: 'var(--muted-foreground)',
                  fontFamily: 'var(--font-atkinson)',
                  fontSize: '11px',
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                (Click a badge to set a goal.)
              </p>
            </div>

            {/* Dynamic Problem Tree Container */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
                minHeight: 0,
              }}
            >
              <DynamicProblemTree
                problemData={PROBLEM_TREE_STRUCTURE}
                nodeSize={64}
                horizontalGap={40}
                verticalGap={80}
                renderNode={renderNode}
                isNodeUnlocked={isNodeUnlocked}
                className="tw-max-w-md"
              />
            </div>
          </div>
        </Panel>
      )}

      {/* Animations for checkpoints */}
      <style>{`
        @keyframes checkboxPop { 0% { transform: scale(0.5); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes checkmarkDraw {
          0% { transform: scale(0) rotate(-45deg); opacity: 0; }
          50% { transform: scale(1.1) rotate(5deg); }
          100% { transform: scale(1) rotate(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ProblemTreePanel;
